// src/components/PendingQueryTrack.jsx
//
// Pending Query Status Track — every outstanding query, who is holding it, and
// what state it is in.
//
// The dashboard already counts pending work several ways: per client on the
// Company Status Overview, per team on the workload cards, per person in the
// modal behind a team's PENDING number. All of them are counts. None of them
// answers the question somebody actually asks about a backlog — "which query,
// with whom, and how long has it been sitting there" — without first choosing
// a team or a client to look inside.
//
// It lists MANUALLY RAISED queries only — work somebody typed in on the task
// screens. The generated ones outnumber them nine to one and are a different
// question; mixed together the list stops being readable as a work queue.
//
// So this is the flat list, at the foot of the page, with a filter on every
// column. Filtering rather than grouping because the question changes: today
// it is "what has Nitheeshkumar got", tomorrow "what is On Hold for KAS", and
// a fixed grouping answers one of those and obstructs the other.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import ReportTable from "./ReportTable";
import { teamPendingTasksApis } from "../redux/dashboard/dashboardActions";
import { formatDateTimeDisplay } from "../utils/common";
import { getStatusColor } from "../utils/badgeColors";
import { useTabRefresh } from "../context/TabsContext";

/* Unassigned is not a person, and it is the single largest bucket in the
   backlog — worth naming rather than leaving blank, so it can be filtered
   for like any other value. */
const UNCLAIMED = "Unclaimed";

const PendingQueryTrack = () => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  /* What the table is actually showing. ReportTable filters internally, so
     without this the summary would describe the whole backlog while the rows
     above it showed one person's — a footer that contradicts the table it
     belongs to is worse than no footer. */
  const [shown, setShown] = useState([]);

  /* Pulled out of the mount effect so the tab hook can call it again.
   *
   * This panel used to fetch once and never again. The dashboard pane stays
   * mounted while another tab is on screen, so a status changed on Development
   * Tasks left this list showing what it read when the tab first opened — a
   * query held at 13:13 still read In Progress here at 13:16. The procedure
   * was right the whole time; the screen was old. */
  const load = useCallback(() => {
    let alive = true;
    setLoading(true);

    /* Manually raised only.
     *
     * No teamCode and no status — the procedure reads those as "do not
     * filter", and the rest of the filtering happens in the table below where
     * the reader can change their mind without a round trip. manualOnly is
     * the exception: it is not a view of the backlog, it is which backlog.
     * Generated queries are the other 1,187 rows and belong to a different
     * question. */
    dispatch(teamPendingTasksApis({ manualOnly: 1 }))
      .unwrap()
      .then((res) => {
        if (!alive) return;
        const body = res?.data ?? res;
        const inner = body?.data ?? body;
        setRows(Array.isArray(inner) ? inner : []);
      })
      .catch(() => alive && setRows([]))
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [dispatch]);

  useEffect(() => load(), [load]);

  /* Re-read whenever the Dashboard tab is clicked, the same way every other
     panel on it does. */
  useTabRefresh(load);

  /* Normalised once, so the filters, the sort and the cell all read the same
     value. A select filter built from raw rows would offer `null` as a choice
     and match nothing when picked. */
  const data = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        AssigneeName: String(r.AssigneeName || "").trim() || UNCLAIMED,
        ClientName: String(r.ClientName || "").trim() || "-",
        TeamName: String(r.TeamName || "").trim() || "No team",
        FormName: String(r.FormName || "").trim() || "-",
        Priority: String(r.Priority || "").trim() || "-",
        /* Where the query came from — CLIENT SIDE or OWN. Defaulted the same
           way the API does, so the filter never offers a blank choice. */
        QueryFrom: String(r.QueryFrom || "").trim() || "CLIENT SIDE",
      })),
    [rows],
  );

  /* Every select filter's options come from the data itself, so a status or a
     person that is not in the backlog is not offered — a dropdown listing
     choices that return nothing is a dropdown that wastes a click. */
  const optionsFor = useCallback(
    (key) => {
      const seen = new Set(data.map((r) => r[key]).filter(Boolean));
      return [...seen]
        .sort((a, b) => String(a).localeCompare(String(b)))
        .map((v) => ({ value: v, label: v }));
    },
    [data],
  );

  const columns = useMemo(
    () => [
      {
        label: "Query No",
        key: "QueryNo",
        width: "100px",
        render: (r) => <span className="fw-bold text-primary">#{r.QueryNo}</span>,
      },
      {
        label: "Query From",
        key: "QueryFrom",
        width: "130px",
        filterType: "select",
        options: optionsFor("QueryFrom"),
        render: (r) => (
          <span
            className={
              r.QueryFrom === "OWN" ? "text-primary fw-semibold" : "text-muted"
            }
          >
            {r.QueryFrom}
          </span>
        ),
      },
      {
        label: "Assigned To",
        key: "AssigneeName",
        width: "150px",
        filterType: "select",
        options: optionsFor("AssigneeName"),
        render: (r) => (
          <span
            className={
              r.AssigneeName === UNCLAIMED ? "text-warning fw-semibold" : ""
            }
          >
            {r.AssigneeName}
          </span>
        ),
      },
      {
        label: "Status",
        key: "Status",
        width: "130px",
        filterType: "select",
        options: optionsFor("Status"),
        render: (r) => (
          <span
            className="fw-semibold"
            style={{ color: getStatusColor(r.Status) }}
          >
            {r.Status}
          </span>
        ),
      },
      {
        label: "Client",
        key: "ClientName",
        width: "160px",
        filterType: "select",
        options: optionsFor("ClientName"),
      },
      {
        label: "Team",
        key: "TeamName",
        width: "130px",
        filterType: "select",
        options: optionsFor("TeamName"),
      },
      {
        label: "Menu",
        key: "FormName",
        width: "150px",
      },
      {
        label: "Task",
        key: "TaskName",
        width: "260px",
        cellClass: "text-cell-wrap",
        /* The name is what it was called; the query is what was asked for.
           Falls back so a row is never blank, and the filter follows. */
        searchValue: (r) => r.TaskName || r.NatureofQuery || "",
        sortValue: (r) => r.TaskName || r.NatureofQuery || "",
        render: (r) => (
          <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            {r.TaskName || r.NatureofQuery || "-"}
          </div>
        ),
      },
      {
        label: "Priority",
        key: "Priority",
        width: "110px",
        filterType: "select",
        options: optionsFor("Priority"),
      },
      {
        label: "Age",
        key: "AgeDays",
        width: "90px",
        /* Sorted as a number, shown as days — "10d" sorts before "9d" as
           text, which puts the oldest item in the middle of the list. */
        sortValue: (r) => Number(r.AgeDays) || 0,
        searchValue: (r) => String(Number(r.AgeDays) || 0),
        render: (r) => (
          <span
            className={Number(r.AgeDays) > 30 ? "text-danger fw-bold" : ""}
          >
            {Number(r.AgeDays) || 0}d
          </span>
        ),
      },
      {
        label: "Raised",
        key: "QueryDate",
        width: "120px",
        type: "date",
        filterType: "date",
        filterDateValue: (r) => r.QueryDate,
        searchValue: (r) => formatDateTimeDisplay(r.QueryDate, "DD/MM/YYYY", ""),
        render: (r) => formatDateTimeDisplay(r.QueryDate, "DD MMM YYYY", "-"),
      },
    ],
    [optionsFor],
  );

  /* Read off the filtered rows, so every figure describes what is on screen.
     Statuses are counted as they come rather than against a fixed list: a
     status nobody has is not a zero worth printing, and a new one appears
     without anybody editing this. */
  const summary = useMemo(() => {
    const byStatus = new Map();
    let unclaimed = 0;
    let stale = 0;
    let oldest = 0;

    shown.forEach((r) => {
      const st = String(r.Status || "").trim() || "No status";
      byStatus.set(st, (byStatus.get(st) || 0) + 1);
      if (r.AssigneeName === UNCLAIMED) unclaimed += 1;
      const age = Number(r.AgeDays) || 0;
      if (age > 30) stale += 1;
      if (age > oldest) oldest = age;
    });

    return {
      statuses: [...byStatus.entries()].sort((a, b) => b[1] - a[1]),
      unclaimed,
      stale,
      oldest,
    };
  }, [shown]);

  const unclaimed = data.filter((r) => r.AssigneeName === UNCLAIMED).length;

  return (
    <div className="content-card mt-4">
      <div className="card-header-custom">
        <h5 className="d-flex align-items-center gap-2 w-100">
          <i className="bi bi-list-check com-text" />
          <span className="com-text">Pending Query Status Track</span>
          <span className="text-muted fw-normal" style={{ fontSize: "0.76rem" }}>
            manually raised
          </span>

          {/* The two numbers worth knowing before reading a row: how much is
              outstanding, and how much of it nobody has picked up. */}
          <span className="ms-auto d-flex align-items-center gap-2">
            <span className="pqt__pill">
              {data.length} pending
            </span>
            {unclaimed > 0 && (
              <span className="pqt__pill pqt__pill--warn">
                {unclaimed} unclaimed
              </span>
            )}
          </span>
        </h5>
      </div>

      {loading ? (
        <div className="text-muted small p-4 text-center">
          Reading the backlog…
        </div>
      ) : (
        <>
          <ReportTable
            data={data}
            columns={columns}
            stickyColumns={[]}
            onDataChange={setShown}
          />

          {/* The backlog's shape, under the rows that make it up. */}
          <div className="pqt__summary">
            <span className="pqt__summary-label">Summary</span>

            {summary.statuses.map(([label, n]) => (
              <span className="pqt__stat" key={label}>
                <i
                  className="bi bi-circle-fill"
                  style={{ color: getStatusColor(label) }}
                />
                {label}
                <b>{n}</b>
              </span>
            ))}

            {summary.unclaimed > 0 && (
              <span className="pqt__stat is-warn">
                <i className="bi bi-person-dash" />
                Unclaimed
                <b>{summary.unclaimed}</b>
              </span>
            )}

            {summary.stale > 0 && (
              <span className="pqt__stat is-stale">
                <i className="bi bi-clock-history" />
                Over 30d
                <b>{summary.stale}</b>
              </span>
            )}

            {summary.oldest > 0 && (
              <span className="pqt__stat">
                <i className="bi bi-hourglass-bottom" />
                Oldest
                <b>{summary.oldest}d</b>
              </span>
            )}

            {/* Says when a filter is hiding something. Without it a filtered
                table looks exactly like a short backlog. */}
            <span className="pqt__showing">
              Showing <strong>{shown.length}</strong> of {data.length}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default PendingQueryTrack;
